import { useContext, useEffect } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

import {
    useCategoryStore,
    useCompanyStore,
    useDisclosureStore,
    useHelperStore,
    useTabStore,
    useTopicStore
} from "~/stores";

import { AdvancedSearchContext } from "~/providers/AdvancedSearchProvider";
import useDefaultAllDisclosures from "./useDefaultAllDisclosures";
import { SEARCH_VIEW_MODE, SEARCH_TABS } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import { useSearchFn } from "./useSearchFn";

const useSearchDialogForm = () => {
    // Store states
    const disclosures = useDisclosureStore(state => state.disclosures);
    const isAllDisclosure = useDisclosureStore(state => state.isAllDisclosuresAvailable);
    const selectedTopicIds = useTopicStore(state => state.selectedTopicIds);
    const totalSelectedDocs = useTopicStore(state => state.totalSelectedDocs);
    const totalCompanies = useCompanyStore(state => state.totalSelected);
    const isFetching = useHelperStore(state => state.isLoading);
    const activeTab = useTabStore(state => state.tab);

    // Store dispatch methods
    const dispatchCategory = useCategoryStore(state => state.dispatch);
    const dispatchDisclosure = useDisclosureStore(state => state.dispatch);
    const dispatchTopics = useTopicStore(state => state.dispatch);
    const dispatchCompanies = useCompanyStore(state => state.dispatch);
    const dispatchHelper = useHelperStore(state => state.dispatch);

    // Store methods
    const getSelectedDisclosureIds = useCategoryStore(state => state.getSelectedDisclosureIds);
    const activeDisclosure = useDisclosureStore(state => state.disclosure);

    const { updateSelectedDisclosures, updateSelectedTopics } = useSearchFn();

    const totalSelectedItems =
        activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS ? totalCompanies : totalSelectedDocs;

    // Advanced search methods
    const { setActiveTabs } = useContext(AdvancedSearchContext);

    const { DEFAULT_ALL_DISCLOSURES } = useDefaultAllDisclosures();
    const navigate = useNavigate();
    const location = useLocation();
    const selectedCategoryIds = getSelectedDisclosureIds();

    useEffect(() => {
        setActiveTabs([activeTab?.key || SEARCH_TABS.IMAGES]);
    }, []);

    const closeModal = (resetDisclosures = false) => {
        dispatchHelper({ type: SearchActions.SET_IS_MODAL_OPEN, payload: false });
        dispatchHelper({
            type: SearchActions.SET_SEARCH_VIEW_MODE,
            payload: SEARCH_VIEW_MODE.SELECTION
        });

        if (resetDisclosures && isAllDisclosure) {
            dispatchDisclosure({
                type: SearchActions.SET_ACTIVE_DISCLOSURE,
                payload: DEFAULT_ALL_DISCLOSURES
            });
        }
    };

    const resetOptions = () => {
        dispatchDisclosure({ type: SearchActions.RESET_DISCLOSURE_CATEGORIES, payload: true });
        dispatchCategory({ type: SearchActions.RESET_CATEGORY_TOPICS, payload: true });
        dispatchTopics({ type: SearchActions.SET_SELECTED_TOPICS, payload: [] });
        dispatchCompanies({ type: SearchActions.RESET_SELECTION });
    };

    const resetSelection = (shouldCloseModal = false) => {
        unstable_batchedUpdates(() => {
            resetOptions();

            if (shouldCloseModal) {
                closeModal(true);
            }
        });
    };

    const openModal = (value: boolean) => {
        dispatchHelper({ type: SearchActions.SET_IS_MODAL_OPEN, payload: value });
        dispatchHelper({
            type: SearchActions.SET_SEARCH_VIEW_MODE,
            payload: SEARCH_VIEW_MODE.SELECTION
        });
    };

    const handleSetShowDialog = () => {
        if (activeTab?.key !== SEARCH_TABS.KEYWORDS) {
            openModal(true);

            if (activeDisclosure?.exploreId === -1) {
                // Set the default Disclosure
                dispatchDisclosure({
                    type: SearchActions.SET_ACTIVE_DISCLOSURE,
                    payload: disclosures[0]
                });
            }
        }
    };

    const searchResultProcess = () => {
        closeModal(false);
        //If multiple Disclosure should still point to All Disclosure
        if (selectedCategoryIds.length > 1) {
            dispatchDisclosure({
                type: SearchActions.SET_ACTIVE_DISCLOSURE,
                payload: DEFAULT_ALL_DISCLOSURES
            });
        }
        navigate("/search/result");
    };

    const handleSearchBtnClick = () => {
        updateSelectedDisclosures();
        updateSelectedTopics();

        const TIMEOUT_MS = 800;
        location.pathname === "/search"
            ? setTimeout(searchResultProcess, TIMEOUT_MS)
            : searchResultProcess();
    };

    return {
        selectedTopicIds,
        isFetching,
        totalSelectedItems,
        resetSelection,
        handleSearchBtnClick,
        handleSetShowDialog,
        closeModal,
        resetOptions,
        openModal
    };
};

export default useSearchDialogForm;
